import FundRaisin from "./abi/FundRaisin.json";
import { ethers } from "ethers";
import { logInfo, logError } from "../utils/log";

// Contract state management
let ContractSigner = null;
let Provider = null;
let contractAddress = null;
let isInitialized = false;

// Wallet connection callback - will be set by WalletContext
let connectWalletCallback = null;

function fundRaisin() { }

// Auto-connect validation helper
const validateContractSigner = async () => {
  if (!isInitialized || !ContractSigner || !Provider) {
    logInfo("Contract not initialized, attempting to connect wallet...");

    if (connectWalletCallback && typeof connectWalletCallback === 'function') {
      try {
        const connected = await connectWalletCallback();
        if (!connected) {
          throw new Error("Wallet connection failed. Please connect your wallet to continue.");
        }

        // Wait a moment for initialization to complete
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Check again after connection
        if (!isInitialized || !ContractSigner || !Provider) {
          throw new Error("Contract initialization failed after wallet connection. Please try again.");
        }

        logInfo("Contract initialized successfully after wallet connection");
      } catch (error) {
        logError("Auto-connect failed:", error);
        throw new Error("Please connect your wallet to perform this operation.");
      }
    } else {
      throw new Error("Wallet not connected. Please connect your wallet to continue.");
    }
  }
};

const isTransactionMined = async (transactionHash) => {
  if (!Provider) {
    throw new Error("Provider not available for transaction monitoring");
  }

  try {
    const txn = await Provider.waitForTransaction(transactionHash, 2);
    logInfo(`Transaction mined: ${transactionHash} at ${new Date()}`);

    if (txn && txn.blockNumber && txn.status > 0) {
      return txn;
    } else if (txn.status === 0) {
      await getFailedTransaction(transactionHash);
    } else {
      return false;
    }
  } catch (error) {
    logError(`Transaction mining failed for ${transactionHash}:`, error);
    throw error;
  }
};

const getFailedTransaction = async (transactionHash) => {
  if (!Provider) {
    throw new Error("Provider not available for transaction analysis");
  }

  try {
    const tx = await Provider.getTransaction(transactionHash);
    await Provider.call(tx, tx.blockNumber);
  } catch (err) {
    logError(`Failed transaction analysis for ${transactionHash}:`, err);
    throw err;
  }
};

fundRaisin.initializeWithSigner = async (contractAddr, provider, address) => {
  try {
    if (!contractAddr || !provider || !address) {
      throw new Error("Missing required parameters for contract initialization");
    }

    // Store contract configuration
    contractAddress = contractAddr;
    Provider = provider;

    // Create contract signer
    const signer = provider.getSigner(address);
    ContractSigner = new ethers.Contract(contractAddr, FundRaisin.abi, signer);

    // Mark as initialized
    isInitialized = true;

    logInfo(
      `Fund Raisin contract initialized successfully with signer ${address.slice(0, 6)}...${address.slice(
        address.length - 4
      )} at address ${contractAddr}`
    );

    return true;
  } catch (error) {
    isInitialized = false;
    ContractSigner = null;
    logError(`Contract initialization failed:`, error);
    throw error;
  }
};

// Re-initialize with new signer (for account changes)
fundRaisin.updateSigner = async (provider, address) => {
  try {
    if (!contractAddress) {
      throw new Error("Contract address not set. Initialize contract first.");
    }

    Provider = provider;
    const signer = provider.getSigner(address);
    ContractSigner = new ethers.Contract(contractAddress, FundRaisin.abi, signer);

    // Mark as initialized
    isInitialized = true;

    logInfo(`Contract signer updated to ${address.slice(0, 6)}...${address.slice(address.length - 4)}`);
    return true;
  } catch (error) {
    logError(`Signer update failed:`, error);
    throw error;
  }
};

// Set wallet connection callback (called by WalletContext)
fundRaisin.setConnectWalletCallback = (callback) => {
  connectWalletCallback = callback;
  logInfo("Wallet connection callback set for auto-connect functionality");
};

fundRaisin.getABI = () => {
  return FundRaisin.abi;
};

fundRaisin.mint = async (mintRequest, address, price) => {
  try {
    // Validate contract is initialized (auto-connect if needed)
    await validateContractSigner();

    // Validate parameters
    if (!mintRequest || !address || price === undefined || price === null) {
      throw new Error("Missing required parameters for minting");
    }

    // Check if mintNFTtoken method exists before calling
    if (typeof ContractSigner.mintNFTtoken !== 'function') {
      throw new Error("mintNFTtoken method not found on contract. Contract may not be properly initialized.");
    }

    const options = {
      value: ethers.utils.parseEther(price.toString()),
    };

    logInfo(`Attempting to mint with price: ${price} ETH for address: ${address}`);
    logInfo(`Mint request:`, mintRequest);

    let tx = await ContractSigner.mintNFTtoken(mintRequest, address, options);

    if (!tx || !tx.hash) {
      throw new Error("Transaction failed or returned invalid response");
    }

    logInfo(`Mint transaction sent: ${tx.hash}`);
    tx = await isTransactionMined(tx.hash);

    return tx;
  } catch (error) {
    logError(`Mint failed:`, error);

    // More specific error handling
    if (error.message.includes("wallet") || error.message.includes("connect")) {
      throw new Error("Wallet connection required. Please connect your wallet and try again.");
    } else if (error.message.includes("mintNFTtoken")) {
      throw new Error("Contract method 'mintNFTtoken' not found. Please check contract ABI and address.");
    } else if (error.code === 'CALL_EXCEPTION') {
      throw new Error("Contract call failed. Check contract state and parameters.");
    } else if (error.code === 'INSUFFICIENT_FUNDS') {
      throw new Error("Insufficient funds for transaction.");
    } else if (error.code === 'USER_REJECTED') {
      throw new Error("Transaction rejected by user.");
    }

    throw error;
  }
};

fundRaisin.buyNft = async (siteId, siteTokenIndex, tokenId, price) => {
  try {
    // Validate contract is initialized (auto-connect if needed)
    await validateContractSigner();

    if (!ContractSigner.buyToken) {
      throw new Error("buyToken method not found on contract");
    }

    const options = {
      value: ethers.utils.parseEther(price.toString()),
    };

    logInfo(`Attempting to buy NFT: siteId=${siteId}, tokenIndex=${siteTokenIndex}, tokenId=${tokenId}, price=${price} ETH`);

    let tx = await ContractSigner.buyToken(siteId, siteTokenIndex, tokenId, options);

    if (!tx || !tx.hash) {
      throw new Error("Buy transaction failed or returned invalid response");
    }

    logInfo(`Buy transaction sent: ${tx.hash}`);
    tx = await isTransactionMined(tx.hash);
    return tx;
  } catch (error) {
    logError(`Buy NFT failed:`, error);

    // Handle wallet connection errors
    if (error.message.includes("wallet") || error.message.includes("connect")) {
      throw new Error("Wallet connection required. Please connect your wallet and try again.");
    } else if (error.code === 'INSUFFICIENT_FUNDS') {
      throw new Error("Insufficient funds for transaction.");
    } else if (error.code === 'USER_REJECTED') {
      throw new Error("Transaction rejected by user.");
    }

    throw error;
  }
};

fundRaisin.changeTokenPrice = async (siteId, siteIndex, tokenId, price, optForSale) => {
  try {
    // Validate contract is initialized (auto-connect if needed)
    await validateContractSigner();

    if (!ContractSigner.changeTokenPrice) {
      throw new Error("changeTokenPrice method not found on contract");
    }

    logInfo(`Changing token price: siteId=${siteId}, siteIndex=${siteIndex}, tokenId=${tokenId}, price=${price} ETH, forSale=${optForSale}`);

    let tx = await ContractSigner.changeTokenPrice(
      siteId,
      siteIndex,
      tokenId,
      ethers.utils.parseEther(price.toString()),
      optForSale
    );

    if (!tx || !tx.hash) {
      throw new Error("Price change transaction failed or returned invalid response");
    }

    logInfo(`Price change transaction sent: ${tx.hash}`);
    tx = await isTransactionMined(tx.hash);
    return tx;
  } catch (error) {
    logError(`Change token price failed:`, error);

    // Handle wallet connection errors
    if (error.message.includes("wallet") || error.message.includes("connect")) {
      throw new Error("Wallet connection required. Please connect your wallet and try again.");
    } else if (error.code === 'INSUFFICIENT_FUNDS') {
      throw new Error("Insufficient funds for transaction.");
    } else if (error.code === 'USER_REJECTED') {
      throw new Error("Transaction rejected by user.");
    }

    throw error;
  }
};

// Utility functions for debugging and state checking
fundRaisin.isInitialized = () => {
  return isInitialized && !!ContractSigner && !!Provider;
};

fundRaisin.getContractAddress = () => {
  return contractAddress;
};

fundRaisin.getCurrentSigner = async () => {
  if (!ContractSigner || !ContractSigner.signer) {
    return null;
  }

  try {
    return await ContractSigner.signer.getAddress();
  } catch (error) {
    logError("Failed to get current signer address:", error);
    return null;
  }
};

fundRaisin.getAvailableMethods = () => {
  if (!ContractSigner || !ContractSigner.functions) return [];
  return Object.keys(ContractSigner.functions);
};

fundRaisin.getState = () => {
  return {
    isInitialized,
    contractAddress,
    hasProvider: !!Provider,
    hasSigner: !!ContractSigner,
    availableMethods: fundRaisin.getAvailableMethods()
  };
};

// Manual trigger for auto-connect (for testing)
fundRaisin.ensureConnected = async () => {
  await validateContractSigner();
  return fundRaisin.isInitialized();
};

// Reset contract state (for disconnection)
fundRaisin.reset = () => {
  ContractSigner = null;
  Provider = null;
  contractAddress = null;
  isInitialized = false;
  logInfo("Contract state reset");
};

export { fundRaisin };
